import AgyleStepper, { createStepObject } from '../../../components/ui/stepper/agyleStepper';
import {
    Chip,
    Dialog,
    DialogContent,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Paper,
    RadioGroup
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import { useTranslation } from 'react-i18next';
import DoorFrontIcon from '@mui/icons-material/DoorFront';
import MessageBox from '../../../util/messageBox';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useOpenState from '../../../hooks/useOpenState';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import AgyleTextFieldButton from '../../../components/ui/buttons/agyleTextFieldButton';
import AddIcon from '@mui/icons-material/Add';
import AgyleDictionaryField from '../../../components/ui/selector/agyleDictionaryField';
import API from '../../../app/api';
import _ from 'lodash';
import { setSnackbar } from '../../../store/reducers/globals';
import { useDispatch, useSelector } from 'react-redux';
import { AgyleLinkIcon, LaptopIcon, PCIcon, TabletIcon } from '../../../components/icons/agyle';
import clsx from 'clsx';
import useDict from '../../../hooks/useDict';
import Radio from '@mui/material/Radio';
import InformationBox from '../../../components/other/informationBox';
import AgyleSwitch from '../../../components/ui/switch/agyleSwitch';
import { user } from '../../../store/reducers/user';
import moment from 'moment';
import { mapHospitalList, wardsList } from '../../../store/reducers/ward';
import { createMapCode, MAP_TYPE } from '../../../util/mapCreator/mapUtils';
import { filterOptions } from '../../../controls/agyle/ecds';
import { getConfig } from '../../../util/configUtil';
import { emergencyDepartment } from '../../../store/reducers/dicts';
import { useTheme } from '@mui/material/styles';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.default,
    },
    stepper: {
        width: '80%',
        margin: 'auto',
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        marginTop: 15,
        borderRadius: 10,
    },
    stepTitle: {
        padding: 15,
        fontWeight: 'bold',
        fontSize: 'x-large',
    },
    container: {
        padding: 15,
    },
    footer: {
        marginTop: 15,
    },
    center: {
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'fit-content',
    },
    counter: {
        color: theme.palette.primary.main,
    },
    footerButton: {
        display: 'flex',
        flexWrap: 'nowrap',
    },
    label: {
        color: theme.palette.mode === 'light' ? '#000' : '#FFF',
        fontWeight: 'bold',
        margin: '10px 0',
        '&.error': {
            color: theme.palette.error.main,
        },
    },
    ward: {
        color: theme.palette.mode === 'light' ? '#000' : '#FFF',
        fontWeight: 'bold',
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        '& svg': {
            marginRight: 10,
        },
    },
    linkIcon: {
        color: theme.palette.primary.main,
        fontSize: 14,
        marginRight: 5,
        '&.marginLeft': {
            marginLeft: 10,
        },
    },
    row: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 5,
    },
    chip: {
        margin: '0 5px',
        backgroundColor: theme.palette.mode === 'light' ? '#969696' : '#F1F7FF',
        borderColor: theme.palette.mode === 'light' ? '#969696' : '#F1F7FF',
        color: theme.palette.mode === 'light' ? '#ffffff' : theme.palette.primary.main,
    },
    finalChip: {
        margin: '0 5px',
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.background.paper,
        borderRadius: 5,
        textTransform: 'uppercase',
        fontWeight: 'bold',
    },
    summaryPaper: {
        backgroundColor: theme.palette.background.default,
        borderRadius: 10,
        padding: 10,
        marginBottom: 10,
    },
    description: {
        fontStyle: 'italic',
        marginLeft: 5,
    },
    subWardSize: {
        color: 'grey',
        fontSize: 'small',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    divider: {
        margin: '10px 0',
    },
}));

const DEFAULT_CLEANING_STATE = {
    bedEnable: true,
    chairEnable: false,
    trolleyEnable: false,
    discharge: 'manual',
    transfer: 'manual',
    offWard: 'manual',
};

const DEFAULT_FORM_STATE = {
    name: '',
    description: '',
    size: null,
    wardIds: [],
    hospital: null,
    specialtyRestriction: null,
    ageRestriction: null,
    published: false,
    active: false,
    cleaningOptions: getConfig('modules.cleaningRequest.enabled', false) ? DEFAULT_CLEANING_STATE : null,
    type: '',
    department: null,
};

const OPTION_TYPE = [
    { id: 'auto', description: 'Auto Request' },
    { id: 'manual', description: 'Manual' },
    { id: 'prompt', description: 'Notification' },
];

const MAP_TYPE_DICT = [
    { id: MAP_TYPE.ED, description: 'Urgent and Emergency Care Services' },
    { id: MAP_TYPE.INPATIENT, description: 'Patient Care Services' },
];

const WardMapCreation = ({ open, onClose, trudWardList, editObject, handleModifyObject }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();

    // Selector
    const userObject = useSelector(user);
    const wards = useSelector(wardsList);
    const edWards = useSelector(emergencyDepartment);

    // Stepper states
    const [activeStep, setActiveStep] = useState(0);
    const [stateError, setStateError] = useState({
        step1: false,
        step2: false,
        step3: false,
        cleaning: false,
    });
    const [form, setForm] = useState(DEFAULT_FORM_STATE);
    const [mapType, setMapType] = useState({
        type: 'draft',
        active: false,
    });
    const [showDescription, setShowDescription] = useState(false);

    // Configs
    const isCleaningEnabled = getConfig('modules.cleaningRequest.enabled', false);
    const defaultHospitalId = getConfig('modules.wardCreator.defaultHospitalId', false);
    const sdecEnabled = getConfig('modules.sdec.enabled');

    // Spec and Age
    const [tempSpecAge, setTempSpecAge] = useState({ age: [], spec: [] });
    const [tempWardSelected, setTempWardSelected] = useState(null);

    // Dictionaries
    const [hospitals, setHospitals] = useState([]);
    const hospitalList = useSelector(mapHospitalList);
    const [ageRestriction] = useDict('MapAgeRestrictions');
    const [mapSizes] = useDict('MapSizes');
    const [specialties] = useDict('Specialties', '', 'relevent eq true');

    // Open states
    const [isConfirmExitOpen, openConfirmExit, closeConfirmExit] = useOpenState(false, true);
    const [isAddSpecOpen, openAddSpec, closeAddSpec] = useOpenState(false, true);

    // Autofilled age and spec restriction
    const [isAlertUpdateOpen, openAlertUpdate, closeAlertUpdate] = useOpenState(false, true);
    const [wardsToUpdate, setWardsToUpdate] = useState([]);
    const tempWard = useRef(null);
    const filledSpec = useRef([]);

    // Download TRUD and Hospitals dict
    useEffect(() => {
        if (hospitalList?.length > 0 && !editObject) {
            setHospitals(_.sortBy(hospitalList, ['organizationName']));
            if (defaultHospitalId) {
                const defaultHosp = _.find(hospitalList, { __id: defaultHospitalId });
                if (defaultHosp) setForm((prevState) => ({ ...prevState, hospital: defaultHosp }));
            }
        }
    }, [hospitalList]);

    useEffect(() => {
        if (editObject) {
            const obj = _.cloneDeep(editObject);
            let size = null;
            let wards = [];

            _.forEach(mapSizes, (dict) => {
                if (dict.height === obj.height && dict.width === obj.width) {
                    size = dict;
                }
            });

            const isED = obj.type === MAP_TYPE.ED;
            _.forEach(obj?.wardsId, (ward) => {
                const w = _.find(isED ? edWards : trudWardList, { wardCode: ward });
                if (w) wards.push(w);
            });

            obj['size'] = size;
            obj['wardIds'] = wards;
            obj['hospital'] = _.find(hospitalList, { __id: editObject?.hospitalId }) || null;
            if (!obj?.cleaningOptions) {
                obj['cleaningOptions'] = DEFAULT_CLEANING_STATE;
            }
            setForm(obj);
        }
    }, [editObject, mapSizes, trudWardList, edWards]);

    // Auto-complete agne nad spec restriction if they already exist
    useEffect(() => {
        if (!editObject) {
            const clone = _.cloneDeep(form);
            _.forEach(clone.wardIds, (ward) => {
                const find = _.find(wards, (w) => w?.wardsId?.includes(ward?.wardCode) && clone?.hospital?.__id === w?.hospitalId);
                if (find) {
                    if (!clone.specialtyRestriction) clone.specialtyRestriction = {};
                    if (!clone.ageRestriction) clone.ageRestriction = {};

                    clone.ageRestriction[ward.wardCode] = find.ageRestriction?.[ward.wardCode] || [];
                    clone.specialtyRestriction[ward.wardCode] = find.specialtyRestriction?.[ward.wardCode] || [];
                    filledSpec.current = [...filledSpec.current, ward.wardCode];
                }
            });
            if (!_.isEqual(form, clone)) setForm(clone);
        }
    }, [form.wardIds, wards, editObject]);

    const steps = [
        createStepObject(t('Map name and size'), false, stateError.step1),
        createStepObject(t('Linked locations'), false, stateError.step2),
        createStepObject(t('Wards specialties and age restriction'), false, stateError.step3),
    ];
    if (isCleaningEnabled) {
        steps.push(createStepObject(t('Cleaning requests'), false, stateError.cleaning));
    }
    steps.push(createStepObject(t('Check and create'), false, false));

    const handleClose = () => {
        closeConfirmExit();
        onClose();
        setForm(DEFAULT_FORM_STATE);
        setMapType({
            type: 'draft',
            active: false,
        });
    };

    const handleCloseSpec = () => {
        closeAddSpec();
        setTempWardSelected(null);
    };

    const handleAddSpec = () => {
        if (tempWardSelected && tempSpecAge.spec && tempSpecAge.age) {
            setForm((prevState) => ({
                ...prevState,
                specialtyRestriction: {
                    ...prevState.specialtyRestriction,
                    [tempWardSelected.wardCode]: tempSpecAge.spec,
                },
                ageRestriction: {
                    ...prevState.ageRestriction,
                    [tempWardSelected.wardCode]: tempSpecAge.age,
                },
            }));

            if (tempWard.current) {
                setWardsToUpdate((prev) => [...prev, tempWardSelected.wardCode]);
                tempWard.current = null;
            }

            setTempSpecAge({ age: [], spec: [] });
            setTempWardSelected(null);
            closeAddSpec();
        }
    };

    const handleCloseUpdate = () => {
        closeAlertUpdate();
        tempWard.current = null;
    };

    const handleUpdateSpec = () => {
        handleAddSpecToWard(tempWard.current, true);
    };

    const handlePreviousStep = () => {
        setActiveStep((prevState) => prevState - 1);
    };

    const validStep = () => {
        let check = false;
        let error = _.cloneDeep(stateError);
        let customMessage = '';
        switch (activeStep) {
            case 0: {
                check = !_.isEmpty(form.name) && !_.isEmpty(form.size) && !_.isEmpty(form.hospital);
                error.step1 = !check;
                break;
            }
            case 1: {
                check = !_.isEmpty(form.wardIds);
                if (!check) customMessage = 'The location must be selected.';

                // Check if selected multi-ward then all wards have a single map
                if (form.wardIds?.length > 1) {
                    const onlyActive = _.filter(wards, (ward) => (ward?.active || ward?.published) && ward?.wardsId?.length === 1);
                    const checkMaps = _.every(form.wardIds, (ward) =>
                        _.find(onlyActive, (activeWard) => activeWard?.wardsId?.includes(ward?.wardCode)),
                    );
                    check = check && checkMaps;
                    if (!checkMaps)
                        customMessage = 'In order to create a combined map, at least one map must be active/published for each ward.';
                }

                if (check && form.type === MAP_TYPE.ED && !form.wardIds?.[0]?.activityType) {
                    check = false;
                    customMessage = 'The ED location must have an activity type.';
                }

                error.step2 = !check;
                break;
            }
            case 2: {
                let checkSpec = true;
                if (form.type !== MAP_TYPE.ED) {
                    _.forEach(form.wardIds, (ward) => {
                        if (!checkSpec) return;
                        checkSpec =
                            !_.isEmpty(form.ageRestriction?.[ward.wardCode]) && !_.isEmpty(form.specialtyRestriction?.[ward.wardCode]);
                    });
                }
                if (!checkSpec) customMessage = 'Not all wards have chosen specialities and age restrictions.';
                error.step3 = !checkSpec;
                check = checkSpec;
                break;
            }
            case 3:
            case 4:
                check = true;
                break;
            default:
                return false;
        }

        setStateError(error);
        if (!check) {
            const message = customMessage || 'Some of the data has not been filled in.';

            dispatch(setSnackbar({ type: 'warning', message }));
        }
        return check;
    };

    const handleSubmit = () => {
        if (!_.isEmpty(wardsToUpdate)) {
            let filteredWards = _.filter(
                _.cloneDeep(wards),
                (w) => w.hospitalId === form?.hospital?.__id && wardsToUpdate.some((id) => w.wardsId?.includes(id)),
            );
            _.forEach(wardsToUpdate, (wardId) => {
                _.forEach(filteredWards, (ward) => {
                    if (ward.wardsId?.includes(wardId) && _.toLower(wardId) !== 'ed') {
                        ward.specialtyRestriction[wardId] = form.specialtyRestriction[wardId];
                        ward.ageRestriction[wardId] = form.ageRestriction[wardId];
                    }
                });
            });

            let find = _.find(filteredWards, { __id: form?.__id });
            if (editObject && find && !_.isEqual(form.cleaningOptions, find?.cleaningOptions)) {
                find['cleaningOptions'] = form.cleaningOptions;
            }

            const requestBody = {
                maps: filteredWards,
                areas: [],
                beds: [],
            };

            API.updateEntireMap(requestBody)
                .then((res) => {
                    if (res) {
                        dispatch(
                            setSnackbar({
                                type: 'success',
                                message: 'Changes to ward maps have been successfully saved.',
                            }),
                        );
                    } else {
                        dispatch(
                            setSnackbar({
                                type: 'error',
                                message: 'There was a problem when saving changes to the ward map.',
                            }),
                        );
                    }
                })
                .catch((err) => {
                    console.error(err);
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: 'There was a problem when saving changes to the ward map.',
                        }),
                    );
                });
        }

        if (editObject) {
            let clone = _.cloneDeep(form);
            clone['width'] = form.size?.width;
            clone['height'] = form.size?.height;

            // Delete helper var
            delete clone.wardIds;
            delete clone.size;
            delete clone.hospital;

            handleModifyObject(clone);

            handleClose();
        } else {
            const wardsId = form.wardIds.map((w) => w.wardCode);

            let mapObject = {
                name: form.name,
                description: form.description,
                code: createMapCode(wardsId, wards),
                wardsId,
                specialtyRestriction: form.specialtyRestriction,
                ageRestriction: form.ageRestriction,
                hospitalId: form.hospital.__id,
                width: form.size?.width,
                height: form.size?.height,
                type: form.type,
                activityType: form.wardIds?.[0]?.activityType,
            };

            if (mapType.type === 'publish') {
                mapObject = {
                    ...mapObject,
                    published: true,
                    publishedDate: moment().toISOString(),
                    publishedBy: userObject?.userName,
                };
                if (mapType.active === true) {
                    mapObject = {
                        ...mapObject,
                        active: true,
                        activeDate: moment().toISOString(),
                        activeBy: userObject?.userName,
                    };
                }
            }

            API.updateWardMapList(mapObject)
                .then(() => {
                    handleClose();
                })
                .catch((err) => {
                    console.error(err);
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: 'There was a problem when saving changes to the ward map.',
                        }),
                    );
                });
        }
    };

    const handleNextStep = () => {
        if (validStep()) {
            if (activeStep === steps.length - 1) {
                handleSubmit();
            } else {
                setActiveStep((prevState) => prevState + 1);
            }
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleChangeDict = (name, value) => {
        handleChange({ target: { name, value } });
    };

    const getIcon = (type) => {
        switch (type) {
            case 'pc':
                return <PCIcon />;
            case 'laptop':
                return <LaptopIcon />;
            case 'tablet':
                return <TabletIcon />;
            default:
                return null;
        }
    };

    const renderMapName = () => {
        const renderHospitalsList = (props, option) => {
            return (
                <li {...props} key={option?.__id}>
                    <Chip label={option?.__id || 'UNK'} sx={{ mr: 1, minWidth: 70 }} />
                    <span className={classes.ward}>{_.startCase(option?.description?.toLowerCase())}</span>
                </li>
            );
        };

        const renderMapSizeList = (props, option) => {
            return (
                <li {...props} key={option?.__id}>
                    <div className={classes.ward}>
                        {getIcon(option.type)}
                        {option?.__description ? (
                            <div>
                                {option?.__description} <br />{' '}
                                <span className={classes.subWardSize}>{`${option.width} x ${option.height}`}</span>
                            </div>
                        ) : (
                            <span>{`${option.width} x ${option.height}`}</span>
                        )}
                    </div>
                </li>
            );
        };

        return (
            <>
                <FormLabel className={clsx(classes.label, stateError.step1 && _.isEmpty(form.name) && 'error')}>{t('Map Name')}</FormLabel>

                <AgyleTextField
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    endAdornment={
                        showDescription ? null : (
                            <AgyleTextFieldButton
                                label={t('Add description')}
                                onClick={() => setShowDescription(true)}
                                startIcon={<AddIcon />}
                            />
                        )
                    }
                />

                {showDescription && (
                    <>
                        <FormLabel className={classes.label}>{t('Map Description')}</FormLabel>

                        <AgyleTextField
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            endAdornment={
                                <AgyleTextFieldButton
                                    label={t('Cancel')}
                                    onClick={() => {
                                        setShowDescription(false);
                                        setForm((prevState) => ({
                                            ...prevState,
                                            description: '',
                                        }));
                                    }}
                                />
                            }
                        />
                    </>
                )}

                <FormLabel className={clsx(classes.label, stateError.step1 && _.isEmpty(form.hospital) && 'error')}>
                    {t('Hospital')}
                </FormLabel>

                <AgyleDictionaryField
                    options={hospitals}
                    onChange={(_e, val) => {
                        handleChangeDict('hospital', val);
                        handleChangeDict('wardIds', []);
                    }}
                    value={form.hospital}
                    getOptionLabel={(option) => option?.description}
                    renderLocalOption={renderHospitalsList}
                    filterOptions={(opt, state) => filterOptions(opt, state, ['description', '__id'])}
                    disabled={!!editObject}
                />

                <FormLabel className={clsx(classes.label, stateError.step1 && _.isEmpty(form.size) && 'error')}>{t('Map Size')}</FormLabel>

                <AgyleDictionaryField
                    options={mapSizes}
                    onChange={(_e, val) => handleChangeDict('size', val)}
                    value={form.size}
                    getOptionLabel={(option) =>
                        `${option?.__description ? option?.__description + ' ' : ''}${option.width} x ${option.height}`
                    }
                    renderLocalOption={renderMapSizeList}
                />
            </>
        );
    };

    const filterDepartures = useCallback(() => {
        let filteredWards = _.filter(edWards, (ward) => ward?.facility === form.hospital?.__id);
        filteredWards = filteredWards.sort((a, b) => {
            const typeA = a?.serviceType;
            const typeB = b?.serviceType;
            return typeA === typeB ? a?.wardName?.localeCompare(b?.wardName) : typeA?.localeCompare(typeB);
        });
        return filteredWards;
    }, [edWards, form.hospital]);

    const filterDisabledDepartures = (option) => {
        if (form.type === MAP_TYPE.ED && !sdecEnabled) {
            return option?.serviceType === 'SDEC';
        }

        return false;
    };

    const getLocationChipColor = (opt) => {
        let backgroundColor;
        let color = '#ffffff';
        switch (opt?.serviceType) {
            case 'ED':
                backgroundColor = theme.palette.attendanceType.ed;
                break;
            case 'UTC':
                backgroundColor = theme.palette.attendanceType.utc;
                color = '#202020';
                break;
            case 'SDEC':
                backgroundColor = theme.palette.attendanceType.sdec;
                break;
            default:
                backgroundColor = theme.palette.attendanceType.ip;
        }
        return { backgroundColor, color };
    };

    const renderTRUD = () => {
        const renderWardList = (props, option) => {
            return (
                <li {...props} key={option?.__id}>
                    <Chip
                        label={option?.wardCode || 'UNK'}
                        sx={{ mr: 1, minWidth: 80 }}
                        style={{ ...getLocationChipColor(option), fontWeight: 'bold' }}
                    />
                    <span className={classes.ward}>{_.startCase(option?.wardName?.toLowerCase())}</span>
                </li>
            );
        };

        const handleChangeWardIds = (_e, val) => {
            handleChangeDict('wardIds', val ? [val] : []);
        };

        const findVal = (val) => {
            return _.find(MAP_TYPE_DICT, { id: val });
        };

        return (
            <>
                <FormLabel className={clsx(classes.label, stateError.step2 && _.isEmpty(form.type) && 'error')}>
                    {t('Locations Type')}
                </FormLabel>

                <AgyleDictionaryField
                    options={MAP_TYPE_DICT}
                    onChange={(_e, val) => {
                        handleChangeDict('type', val?.id ?? null);
                        handleChangeDict('wardIds', []);
                    }}
                    value={findVal(form?.type)}
                    disabled={!!editObject}
                    getOptionLabel={(option) => option?.description}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                />

                {form.type === MAP_TYPE.ED && (
                    <>
                        <FormLabel className={clsx(classes.label, stateError.step2 && _.isEmpty(form.wardIds) && 'error')}>
                            {t('Linked Locations')}
                        </FormLabel>

                        <AgyleDictionaryField
                            options={filterDepartures()}
                            onChange={handleChangeWardIds}
                            value={form.wardIds?.[0] ?? null}
                            getOptionLabel={(option) => option.wardName}
                            renderLocalOption={renderWardList}
                            disabled={!!editObject}
                            filterOptions={(opt, state) => filterOptions(opt, state, ['wardCode', 'wardName'])}
                            groupBy={(opt) => opt.serviceType}
                            getOptionDisabled={(option) => filterDisabledDepartures(option)}
                        />
                    </>
                )}

                {form.type === MAP_TYPE.INPATIENT && (
                    <>
                        <FormLabel className={clsx(classes.label, stateError.step2 && _.isEmpty(form.wardIds) && 'error')}>
                            {t('Linked Locations')}
                        </FormLabel>

                        <AgyleDictionaryField
                            options={trudWardList}
                            onChange={handleChangeWardIds}
                            value={form.wardIds?.[0] ?? null}
                            getOptionLabel={(option) => option.wardName}
                            renderLocalOption={renderWardList}
                            disabled={!!editObject}
                            filterOptions={(opt, state) => filterOptions(opt, state, ['wardCode', 'wardName'])}
                        />
                    </>
                )}
            </>
        );
    };

    const handleAddSpecToWard = (ward, force) => {
        if (!force && filledSpec.current?.includes(ward.wardCode)) {
            tempWard.current = ward;
            openAlertUpdate();
        } else if (ward) {
            closeAlertUpdate();
            setTempSpecAge({
                age: form?.ageRestriction?.[ward.wardCode] || [],
                spec: form?.specialtyRestriction?.[ward.wardCode] || [],
            });
            setTempWardSelected(ward);
            openAddSpec();
        }
    };

    const renderSpecialities = () => {
        const haveED = form.type === MAP_TYPE.ED;
        return (
            <>
                <FormLabel className={classes.label}>{t('Select specialties and age restrictions for these locations')}</FormLabel>

                {haveED && (
                    <InformationBox type="info">
                        {t('No specializations or age restrictions required for Emergency Services')}
                    </InformationBox>
                )}

                {!haveED &&
                    form.wardIds.map((ward) => (
                        <div key={ward.__id} className={classes.row}>
                            <AgyleLinkIcon className={clsx(classes.linkIcon, 'marginLeft')} />

                            <span className={classes.ward}>
                                {_.startCase(ward?.wardName.toLowerCase())} [{ward?.wardCode}]
                            </span>

                            <div style={{ marginLeft: 10 }}>
                                <AgyleButton
                                    onClick={handleAddSpecToWard.bind(this, ward, false)}
                                    startIcon={<AddIcon />}
                                    size="small"
                                    variant="contained"
                                    disabled={ward?.wardCode === 'ED'}
                                >
                                    {t('Add')}
                                </AgyleButton>
                            </div>

                            {!_.isEmpty(form?.specialtyRestriction?.[ward.wardCode]) &&
                                form?.specialtyRestriction?.[ward.wardCode].map((spec) => (
                                    <Chip key={spec.__id} label={_.startCase(spec?.__description.toLowerCase())} className={classes.chip} />
                                ))}

                            {!_.isEmpty(form?.specialtyRestriction?.[ward.wardCode]) &&
                                !_.isEmpty(form?.ageRestriction?.[ward.wardCode]) && <Divider orientation="vertical" flexItem />}

                            {!_.isEmpty(form?.ageRestriction?.[ward.wardCode]) &&
                                form?.ageRestriction?.[ward.wardCode].map((age) => (
                                    <Chip key={age.__id} label={age?.__description} className={classes.chip} />
                                ))}
                        </div>
                    ))}
            </>
        );
    };

    const renderConfirmCheck = () => {
        return (
            <>
                <Paper className={classes.summaryPaper} style={{ padding: 15 }}>
                    <div>
                        <FormLabel className={classes.label}>{t('Map Name')}:</FormLabel>
                        <span className={classes.description}>{form.name}</span>
                    </div>

                    {form.description !== '' && (
                        <div>
                            <FormLabel className={classes.label}>{t('Description')}:</FormLabel>

                            <span className={classes.description}>{form.description}</span>
                        </div>
                    )}

                    <div>
                        <FormLabel className={classes.label}>{t('Hospital')}:</FormLabel>

                        <span className={classes.description}>{form.hospital?.organizationName}</span>
                    </div>

                    <div>
                        <FormLabel className={classes.label}>{t('Map size')}:</FormLabel>

                        <span className={classes.description}>
                            {`${form.size?.__description ? form.size?.__description + ' ' : ''}${form.size?.width} x ${form.size?.height}`}
                        </span>
                    </div>

                    <div>
                        <FormLabel className={classes.label}>{t('Locations Type')}:</FormLabel>

                        <span className={classes.description}>
                            {form?.type ? MAP_TYPE_DICT.find((type) => type.id === form?.type)?.description : ''}
                        </span>
                    </div>
                </Paper>

                {form.wardIds.map((ward) => (
                    <Paper key={ward.__id} className={classes.summaryPaper} style={{ padding: 15 }}>
                        <div className={classes.divRow}>
                            <AgyleLinkIcon className={classes.linkIcon} />

                            <span className={classes.ward}>
                                {_.startCase(ward?.wardName.toLowerCase())} [{ward?.wardCode}]
                            </span>
                        </div>

                        <Grid container direction="column">
                            <FormLabel className={classes.label}>{t('Location Specialization')}</FormLabel>

                            <div>
                                {!_.isEmpty(form?.specialtyRestriction?.[ward.wardCode]) &&
                                    form?.specialtyRestriction?.[ward.wardCode].map((spec) => (
                                        <Chip
                                            key={spec.__id}
                                            label={_.startCase(spec?.__description.toLowerCase())}
                                            className={classes.finalChip}
                                        />
                                    ))}
                                {_.isEmpty(form?.specialtyRestriction?.[ward.wardCode]) && '-'}
                            </div>
                        </Grid>

                        <Grid container direction="column">
                            <FormLabel className={classes.label}>{t('Age Restriction')}</FormLabel>

                            <div>
                                {!_.isEmpty(form?.ageRestriction?.[ward.wardCode]) &&
                                    form?.ageRestriction?.[ward.wardCode].map((age) => (
                                        <Chip key={age.__id} label={age?.__description} className={classes.finalChip} />
                                    ))}
                                {_.isEmpty(form?.ageRestriction?.[ward.wardCode]) && '-'}
                            </div>
                        </Grid>
                    </Paper>
                ))}
            </>
        );
    };

    const renderCleaningRequestOptions = () => {
        const handleChangeCleaningOption = (e) => {
            const { name, checked } = e.target;
            if (form?.wardIds?.[0]?.wardCode && !wardsToUpdate?.includes(form?.wardIds?.[0]?.wardCode)) {
                setWardsToUpdate((prev) => [...prev, form?.wardIds?.[0]?.wardCode]);
            }
            setForm((prevState) => ({
                ...prevState,
                cleaningOptions: {
                    ...prevState.cleaningOptions,
                    [name]: checked,
                },
            }));
        };

        const renderEventType = (type) => {
            const findVal = (val) => {
                return _.find(OPTION_TYPE, { id: val });
            };

            const handleChangeOption = (_e, val) => {
                if (form?.wardIds?.[0]?.wardCode && !wardsToUpdate?.includes(form?.wardIds?.[0]?.wardCode)) {
                    setWardsToUpdate((prev) => [...prev, form?.wardIds?.[0]?.wardCode]);
                }
                setForm((prevState) => ({
                    ...prevState,
                    cleaningOptions: {
                        ...prevState.cleaningOptions,
                        [type]: val?.id,
                    },
                }));
            };

            return (
                <div>
                    <FormLabel>{`${t('Patient')} ${_.capitalize(type)}`}</FormLabel>

                    <AgyleDictionaryField
                        options={OPTION_TYPE}
                        onChange={handleChangeOption}
                        value={findVal(form?.cleaningOptions?.[type])}
                        getOptionLabel={(option) => option?.description}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    />
                </div>
            );
        };

        return (
            <>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FormLabel>{t('Enable cleaning requests for specific locations')}</FormLabel>

                    <AgyleSwitch
                        label={t('Bed type')}
                        name="bedEnable"
                        onChange={handleChangeCleaningOption}
                        value={form?.cleaningOptions?.bedEnable}
                        style={{ maxHeight: 40 }}
                    />

                    <AgyleSwitch
                        label={t('Chair type')}
                        name="chairEnable"
                        onChange={handleChangeCleaningOption}
                        value={form?.cleaningOptions?.chairEnable}
                        style={{ maxHeight: 40 }}
                    />

                    <AgyleSwitch
                        label={t('Trolley type')}
                        name="trolleyEnable"
                        onChange={handleChangeCleaningOption}
                        value={form?.cleaningOptions?.trolleyEnable}
                        style={{ maxHeight: 40 }}
                    />
                </div>

                <Divider className={classes.divider} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <FormLabel>{t('Configure cleaning requests on specific events')}</FormLabel>
                    {renderEventType('discharge')}

                    {renderEventType('transfer')}

                    {renderEventType('offWard')}
                </div>
            </>
        );
    };

    const renderContent = () => {
        switch (activeStep) {
            case 0:
                return renderMapName();
            case 1:
                return renderTRUD();
            case 2:
                return renderSpecialities();
            case 3:
                return isCleaningEnabled ? renderCleaningRequestOptions() : renderConfirmCheck();
            case 4:
                return renderConfirmCheck();
            default:
                console.warn('Invalid step', activeStep);
                return '';
        }
    };

    const renderConfirmSave = () => {
        const handleChangeType = (event) => {
            const { value } = event.target;
            let data = _.cloneDeep(mapType);
            if (value === 'draft') {
                data.active = false;
            }
            data.type = value;
            setMapType(data);
        };

        const handleSwitchLive = () => {
            setMapType((prevState) => ({ ...prevState, active: !prevState.active }));
        };

        return (
            <>
                <FormLabel className={classes.label}>
                    {t(editObject ? 'Are you sure you want to save these changes?' : 'Save this Map as:')}
                </FormLabel>

                {!editObject && (
                    <Paper className={clsx(classes.summaryPaper, classes.footer)}>
                        <FormControl>
                            <RadioGroup value={mapType.type} onChange={handleChangeType}>
                                <FormControlLabel value="publish" control={<Radio />} label={t('Published')} />
                                {mapType.type === 'publish' && (
                                    <div style={{ maxWidth: 350 }}>
                                        <span style={{ fontSize: 12 }}>{t('Would you like to make this map live?')}</span>

                                        <InformationBox type="hint">
                                            {t(
                                                'Enabling live mode allows you to activate the created map and display it on device screens.',
                                            )}
                                        </InformationBox>

                                        <div className={classes.center}>
                                            <AgyleSwitch label={t('Live Mode')} onChange={handleSwitchLive} value={mapType.active} />
                                        </div>
                                    </div>
                                )}
                                <FormControlLabel value="draft" control={<Radio />} label={t('Drafts')} />
                            </RadioGroup>
                        </FormControl>
                    </Paper>
                )}
            </>
        );
    };

    const renderSelectSpec = () => {
        const handleAddSpecDict = (type, val) => {
            setTempSpecAge((prevState) => ({ ...prevState, [type]: val }));
        };

        return (
            <>
                <FormLabel className={classes.label} style={{ color: 'grey' }}>
                    {tempWardSelected?.wardName || 'UNK'}
                </FormLabel>

                <Divider />

                <br />

                <FormLabel className={classes.label}>{t('Specialization')}</FormLabel>

                <AgyleDictionaryField
                    options={specialties}
                    isMultiple
                    onChange={(_e, val) => handleAddSpecDict('spec', val)}
                    value={tempSpecAge.spec}
                    getOptionLabel={(option) => option.__description}
                    noBorder={false}
                    withNewBackground
                />

                <br />

                <FormLabel className={classes.label}>{t('Age Restriction')}</FormLabel>

                <AgyleDictionaryField
                    options={ageRestriction}
                    isMultiple
                    onChange={(_e, val) => handleAddSpecDict('age', val)}
                    value={tempSpecAge.age}
                    getOptionLabel={(option) => option.__description}
                    noBorder={false}
                    withNewBackground
                />
            </>
        );
    };

    if (!open) return null;

    return (
        <>
            <Dialog open={open} fullScreen>
                <DialogContent className={clsx('scroll-normal-white', classes.root)}>
                    <AgyleButton startIcon={<DoorFrontIcon />} onClick={openConfirmExit} colour="error">
                        {t('Exit')}
                    </AgyleButton>

                    <div className={classes.stepper}>
                        <AgyleStepper title={t(editObject ? 'Edit Map' : 'Create Map')} activeStep={activeStep} steps={steps} smallMargin />
                        <Paper className={classes.paper}>
                            <div className={classes.stepTitle}>
                                {t('Step')} {activeStep + 1}
                            </div>

                            <Divider />

                            <Grid className={classes.container}>
                                <Grid container direction="column">
                                    {renderContent()}
                                </Grid>

                                <Grid
                                    container
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    className={classes.footer}
                                >
                                    <div className={classes.counter}>{`${activeStep + 1} / ${steps.length}`}</div>
                                    <div className={classes.footerButton}>
                                        {activeStep !== 0 && (
                                            <AgyleButton onClick={handlePreviousStep} colour="primary">
                                                {t('Previous Step')}
                                            </AgyleButton>
                                        )}

                                        <AgyleButton onClick={handleNextStep} variant="contained">
                                            {t(
                                                activeStep === steps.length - 1
                                                    ? editObject
                                                        ? 'Apply Changes'
                                                        : 'Create Map'
                                                    : 'Next Step',
                                            )}
                                        </AgyleButton>
                                    </div>
                                </Grid>
                            </Grid>
                        </Paper>
                    </div>
                </DialogContent>
            </Dialog>

            <MessageBox
                title={t('Exit Map Creator')}
                haveAccept={{ yes: t('Exit'), no: t('Cancel') }}
                onAccept={handleClose}
                onClose={closeConfirmExit}
                message={t('Changes made in the map creator will be lost if you exit. Are you sure you want to continue?')}
                open={isConfirmExitOpen}
                warning
            />

            <MessageBox
                title={t('Select Specialization and Age Restrictions for Location')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={handleAddSpec}
                onClose={handleCloseSpec}
                messageObject={renderSelectSpec()}
                open={isAddSpecOpen}
            />

            <MessageBox
                open={isAlertUpdateOpen}
                title={t('Update Specialties and Age Restrictions for Location')}
                onAccept={handleUpdateSpec}
                onClose={handleCloseUpdate}
                message={t('Changes will also apply to all other maps that use this location identifier.')}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                warning
            />
        </>
    );
};

export default WardMapCreation;
